import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Gamepad2, X, Infinity as InfinityIcon } from "lucide-react";

import appLogo from "../assets/app/logo.png";
import Games from "../GLOBAL/Games";
import { M, BEVEL_SM, BEVEL_LG } from "../CATEGORIES/UICategories";

/* ═══════════════════════════════════════════════════════════
   LOGO PALETTE — pulled from the eBUOS mark itself
   (blue → purple → pink → orange → yellow → green)
═══════════════════════════════════════════════════════════ */
const LOGO = {
  blue: "#3E6DF2",
  purple: "#9A4FE0",
  pink: "#F0356B",
  orange: "#FF7E1A",
  yellow: "#F5C518",
  green: "#4CB944",
};

const LOGO_GRADIENT = `linear-gradient(90deg, ${LOGO.blue} 0%, ${LOGO.purple} 22%, ${LOGO.pink} 42%, ${LOGO.orange} 62%, ${LOGO.yellow} 80%, ${LOGO.green} 100%)`;

/* ═══════════════════════════════════════════════════════════
   PALETTE — pulled straight from CentralHome's `M` tokens,
   so this page can never drift from the rest of eBUOS.
═══════════════════════════════════════════════════════════ */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');
  .mo-cs, .mo-cs *, .mo-cs *::before, .mo-cs *::after { box-sizing: border-box; }
  .mo-cs { font-family: 'Space Grotesk', sans-serif; color: ${M.pearl}; background: ${M.blueInk}; }
  .mo-cs h1, .mo-cs h2 { font-family: 'Fredoka', sans-serif; font-weight: 600; margin: 0; letter-spacing: -0.02em; line-height: 1; }
  .mo-cs button { cursor: pointer; border: none; background: none; font-family: inherit; color: inherit; }

  .mo-mono{ font-family:'IBM Plex Mono',ui-monospace,monospace; font-weight:600; font-size:10.5px; letter-spacing:.22em; text-transform:uppercase; }

  @keyframes moBob{ 0%,100%{transform:translateY(0) rotate(var(--tilt,0deg));} 50%{transform:translateY(-14px) rotate(var(--tilt,0deg));} }
  @keyframes moSpin{ to{ transform:rotate(360deg); } }
  @keyframes moTrace{ to{ stroke-dashoffset:0; } }
  @keyframes moGlint{ 0%,100%{opacity:.25; transform:scale(.7);} 50%{opacity:1; transform:scale(1.1);} }
  @keyframes moHue{ 0%{ filter:hue-rotate(0deg); } 100%{ filter:hue-rotate(360deg); } }
  .mo-bob{ animation:moBob 6s ease-in-out infinite; }
  .mo-loop-glint{ animation:moGlint 3.4s ease-in-out infinite; }

  .mo-cs-sky{
    position:fixed; inset:0; z-index:0; pointer-events:none;
    background:
      radial-gradient(60% 46% at 8% 6%, rgba(62,109,242,.42) 0%, transparent 62%),
      radial-gradient(50% 40% at 34% -4%, rgba(154,79,224,.36) 0%, transparent 60%),
      radial-gradient(46% 40% at 60% 2%, rgba(240,53,107,.30) 0%, transparent 58%),
      radial-gradient(46% 38% at 82% 10%, rgba(255,126,26,.26) 0%, transparent 58%),
      radial-gradient(40% 34% at 100% 22%, rgba(245,197,24,.20) 0%, transparent 58%),
      radial-gradient(44% 40% at 4% 82%, rgba(76,185,68,.16) 0%, transparent 58%),
      linear-gradient(${M.blueInk} 0%, ${M.night} 100%);
  }
  .mo-cs-grid{
    position:fixed; inset:0; z-index:0; pointer-events:none; opacity:.13;
    background-image:linear-gradient(${M.blueLit} 1px, transparent 1px), linear-gradient(90deg, ${M.blueLit} 1px, transparent 1px);
    background-size:74px 74px;
    -webkit-mask:radial-gradient(80% 60% at 50% 8%, #000 0%, transparent 72%);
    mask:radial-gradient(80% 60% at 50% 8%, #000 0%, transparent 72%);
  }
  .mo-cs-stamp{ position:fixed; inset:-20% -20%; z-index:0; pointer-events:none; background-repeat:repeat; transform:rotate(-9deg); opacity:.05; }

  .mo-cs-mark{ display:flex; align-items:center; gap:11px; }
  .mo-cs-mark .chip{
    width:44px; height:44px; border-radius:50%; flex-shrink:0; background:${M.pearl};
    padding:2px; background-clip:padding-box;
    box-shadow:0 0 0 2px ${M.blueInk}, 0 0 0 2.5px transparent;
  }
  .mo-cs-mark .chip-ring{
    width:44px; height:44px; border-radius:50%; flex-shrink:0; padding:3px;
    background:${LOGO_GRADIENT};
  }
  .mo-cs-mark .chip-ring img{ width:100%; height:100%; object-fit:cover; border-radius:50%; display:block; background:${M.pearl}; }

  .mo-cs-btn{
    display:inline-flex; align-items:center; justify-content:center; gap:9px; white-space:nowrap;
    font-family:'Fredoka',sans-serif; font-weight:600; font-size:14.5px;
    padding:14px 26px; border-radius:999px;
    background:${M.pearl}; color:${M.blueDeep}; box-shadow:${BEVEL_SM}, 0 6px 0 ${M.blueDeep};
    transition:transform .2s cubic-bezier(.2,.9,.3,1.4), box-shadow .2s, background .2s, color .2s;
  }
  .mo-cs-btn:hover{ transform:translateY(-3px); box-shadow:${BEVEL_SM}, 0 9px 0 ${M.blueDeep}; }
  .mo-cs-btn:active{ transform:translateY(3px); box-shadow:${BEVEL_SM}, 0 1px 0 ${M.blueDeep}; }
  .mo-cs-btn.money{ background:linear-gradient(120deg, ${LOGO.orange} 0%, ${LOGO.yellow} 100%); color:${M.blueInk}; box-shadow:${BEVEL_SM}, 0 6px 0 ${M.yellowDeep}; }
  .mo-cs-btn.money:hover{ box-shadow:${BEVEL_SM}, 0 9px 0 ${M.yellowDeep}; }
  .mo-cs-btn.money:active{ box-shadow:${BEVEL_SM}, 0 1px 0 ${M.yellowDeep}; }
  .mo-cs-btn.clear{ background:transparent; color:${M.pearl}; box-shadow:0 0 0 2px rgba(255,255,255,.32); }
  .mo-cs-btn.clear:hover{ background:rgba(255,255,255,.08); box-shadow:0 0 0 2px rgba(255,255,255,.6); transform:translateY(-2px); }
  .mo-cs-btn.sm{ padding:10px 18px; font-size:12.5px; box-shadow:${BEVEL_SM}, 0 4px 0 ${M.blueDeep}; }

  .mo-cs-eyebrow{ display:inline-flex; align-items:center; gap:9px; padding:7px 16px 7px 8px; border-radius:999px;
    background:rgba(255,255,255,.08); box-shadow:0 0 0 2px rgba(255,255,255,.22); }
  .mo-cs-bead{
    width:34px; height:34px; border-radius:50%; flex-shrink:0; padding:0; display:flex; align-items:center; justify-content:center;
    background:radial-gradient(circle at 34% 26%, rgba(255,255,255,.9) 0%, rgba(255,255,255,.12) 36%, transparent 58%), linear-gradient(135deg, ${LOGO.pink} 0%, ${LOGO.orange} 100%);
    color:${M.blueInk}; box-shadow:${BEVEL_SM}, 0 4px 0 ${LOGO.pink};
  }

  .mo-cs-gradient-text{
    background:${LOGO_GRADIENT};
    background-size:200% auto;
    -webkit-background-clip:text; background-clip:text; color:transparent;
  }

  .mo-cs-close{
    position:absolute; top:14px; right:14px; width:38px; height:38px; border-radius:12px;
    display:flex; align-items:center; justify-content:center;
    background:rgba(255,255,255,.1); box-shadow:0 0 0 2px rgba(255,255,255,.2);
    transition:background .16s, transform .16s;
  }
  .mo-cs-close:hover{ background:rgba(255,255,255,.18); transform:scale(1.05); }

  @media (max-width: 768px){
    .mo-cs-head{ font-size: 42px !important; }
    .mo-cs-sub{ font-size: 14.5px !important; }
    .mo-cs-logowrap{ width: 190px !important; height: 190px !important; }
    .mo-cs-btn{ padding: 11px 18px !important; font-size: 13px !important; }
  }
  @media (prefers-reduced-motion: reduce){ .mo-cs *{ animation:none !important; transition:none !important; } }
`;

/* ═══════════════════════════════════════════════════════════
   THE HEART — same heart used inside the logo's "O", now traced
   in the full logo gradient instead of a single flat color.
   Uses pathLength=1 so the trace animation is exact regardless
   of the path's real length.
═══════════════════════════════════════════════════════════ */
const HEART_PATH =
  "M60 105 C25 80, 5 55, 5 30 C5 10, 20 3, 40 6 C50 8, 57 15, 60 22 C63 15, 70 8, 80 6 C100 3, 115 10, 115 30 C115 55, 95 80, 60 105 Z";

const Loop: React.FC<{ size?: number; trace?: boolean; gradient?: boolean; color?: string; style?: React.CSSProperties }> = ({
  size = 120, trace, gradient = true, color = M.chrome, style,
}) => (
  <svg width={size} height={(size / 120) * 112} viewBox="0 0 120 112" style={style} aria-hidden>
    <defs>
      <linearGradient id="moLoopGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={LOGO.blue} />
        <stop offset="22%" stopColor={LOGO.purple} />
        <stop offset="42%" stopColor={LOGO.pink} />
        <stop offset="62%" stopColor={LOGO.orange} />
        <stop offset="80%" stopColor={LOGO.yellow} />
        <stop offset="100%" stopColor={LOGO.green} />
      </linearGradient>
    </defs>
    <path d={HEART_PATH} fill="none" stroke={M.pearl} strokeWidth="15" strokeLinejoin="round" pathLength={1} />
    <path
      d={HEART_PATH}
      fill="none"
      stroke={gradient ? "url(#moLoopGrad)" : color}
      strokeWidth="9"
      strokeLinecap="round"
      pathLength={1}
      strokeDasharray={trace ? 1 : undefined}
      strokeDashoffset={trace ? 1 : undefined}
      style={trace ? { animation: "moTrace 2.6s cubic-bezier(.5,0,.2,1) .3s forwards" } : undefined}
    />
  </svg>
);

/* ═══════════════════════════════════════════════════════════
   WATERMARK — tiled logo, same treatment as CentralHome's mo-stamp
═══════════════════════════════════════════════════════════ */
const Watermark: React.FC = () => (
  <div
    className="mo-cs-stamp"
    style={{ backgroundImage: `url(${appLogo})`, backgroundSize: "260px auto" }}
    aria-hidden
  />
);

/* ═══════════════════════════════════════════════════════════
   WORDMARK — logo only, ring traces the logo's own gradient
═══════════════════════════════════════════════════════════ */
const Wordmark: React.FC<{ size?: number }> = ({ size = 44 }) => (
  <span className="mo-cs-mark" aria-label="eBUOS">
    <span className="chip-ring" style={{ width: size, height: size }}>
      <img src={appLogo} alt="eBUOS" />
    </span>
  </span>
);

/* ═══════════════════════════════════════════════════════════
   COMING SOON
═══════════════════════════════════════════════════════════ */
const ComingSoon: React.FC = () => {
  const navigate = useNavigate();
  const [showGame, setShowGame] = useState(false);

  return (
    <div className="mo-cs" style={{ position: "relative", minHeight: "100vh", width: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <style>{CSS}</style>

      <div className="mo-cs-sky" />
      <div className="mo-cs-grid" />
      <Watermark />

      {/* ── TOP BAR ── */}
      <div
        style={{
          position: "relative", zIndex: 2,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "22px 28px", gap: 16,
        }}
      >
        <motion.div initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
          <Wordmark />
        </motion.div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <motion.button
            initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
            className="mo-cs-btn clear sm"
            onClick={() => setShowGame(true)}
          >
            <Gamepad2 size={15} strokeWidth={2.6} /> Play
          </motion.button>

          <motion.button
            initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="mo-cs-btn money sm"
            onClick={() => navigate("/AuthSwitch")}
          >
            Sign Up <ArrowRight size={14} strokeWidth={2.8} />
          </motion.button>
        </div>
      </div>

      {/* ── CENTER ── */}
      <div
        style={{
          position: "relative", zIndex: 1, flex: 1,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          textAlign: "center", padding: "20px 24px 70px",
        }}
      >
        <span className="mo-cs-eyebrow" style={{ marginBottom: 26 }}>
          <span className="mo-cs-bead"><InfinityIcon size={15} strokeWidth={3} /></span>
          <span className="mo-mono">Something's Loading</span>
        </span>

        <motion.div
          className="mo-cs-logowrap mo-bob"
          initial={{ opacity: 0, scale: 0.86, y: 18 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{
            position: "relative", width: 240, height: 240, marginBottom: 40,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Loop size={280} trace gradient style={{ position: "absolute", opacity: 0.5 }} />
          <img
            src={appLogo}
            alt="eBUOS"
            style={{
              width: "62%", height: "auto", objectFit: "contain",
              filter: "drop-shadow(0 22px 30px rgba(0,0,0,.6))", position: "relative", zIndex: 2,
            }}
          />
          {[
            { t: "10%", l: "8%", c: LOGO.blue, d: "0s" },
            { t: "22%", l: "88%", c: LOGO.pink, d: ".6s" },
            { t: "50%", l: "-4%", c: LOGO.purple, d: "1.1s" },
            { t: "82%", l: "14%", c: LOGO.green, d: "1.5s" },
            { t: "88%", l: "80%", c: LOGO.yellow, d: "2.1s" },
            { t: "50%", l: "102%", c: LOGO.orange, d: "2.6s" },
          ].map((s, i) => (
            <span
              key={i}
              className="mo-loop-glint"
              style={{
                position: "absolute", top: s.t, left: s.l, width: 13, height: 13, borderRadius: "50%",
                background: s.c, boxShadow: `0 0 0 2.5px ${M.pearl}`, animationDelay: s.d,
              }}
            />
          ))}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.15 }}
          className="mo-cs-head"
          style={{ fontSize: 64, lineHeight: 1.02, marginBottom: 18, maxWidth: 780 }}
        >
          Cool Stuff<br />
          <span className="mo-cs-gradient-text">Coming Soon</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
          className="mo-cs-sub"
          style={{ fontSize: 16, fontWeight: 500, color: "rgba(255,255,255,.72)", maxWidth: 480, lineHeight: 1.65, marginBottom: 30 }}
        >
          We're putting the finishing touches on something worth the wait.
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
          className="mo-cs-btn money"
          onClick={() => setShowGame(true)}
        >
          <Gamepad2 size={16} strokeWidth={2.6} /> Play while you wait
        </motion.button>
      </div>

      {/* ── GAME OVERLAY ── */}
      <AnimatePresence>
        {showGame && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: "fixed", inset: 0, zIndex: 10,
              background: "rgba(5,11,31,.72)", backdropFilter: "blur(6px)",
              display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
            }}
            onClick={() => setShowGame(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 12 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "relative", background: M.blueInk,
                borderRadius: 30, padding: 24, maxWidth: 520, width: "100%",
                boxShadow: `${BEVEL_LG}, 0 24px 60px -20px rgba(0,0,0,.6)`,
              }}
            >
              <button className="mo-cs-close" onClick={() => setShowGame(false)} aria-label="Close">
                <X size={17} strokeWidth={2.8} color={M.pearl} />
              </button>
              <Games />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ComingSoon;